/**
 * The consent a signup gives us to CALL them about setting up their account.
 *
 * Shared by the LimePhone register page (client/) and the server, and copied
 * verbatim into the LimeCall dashboard (a separate repo) at
 * src/lib/callConsent.ts. Keep the three in step: the version string is what a
 * stored consent points back to, so it is the only record of which words a
 * person actually agreed to.
 *
 * ⚠ Changing the WORDING means a NEW version. Consent is to a specific
 * sentence; re-labelling old rows with new text would claim people agreed to
 * something they never saw. Add the new version to KNOWN_CALL_CONSENT_VERSIONS
 * and keep the old one there, so a stale browser tab that still shows the old
 * sentence records the old version rather than being refused.
 *
 * Deliberately a separate, OPTIONAL, unticked checkbox — never folded into the
 * terms of service. An automated/AI voice call needs its own express consent,
 * and a pre-ticked box is not consent.
 */
export const CALL_CONSENT_TEXT =
  'Call or text me about setting up my account, including with an automated or AI voice. Optional — you can opt out any time.';

export const CALL_CONSENT_TEXT_VERSION = 'v1-2026-09-24';

/** Every version a client may legitimately send. */
export const KNOWN_CALL_CONSENT_VERSIONS: Readonly<Record<string, string>> = {
  [CALL_CONSENT_TEXT_VERSION]: CALL_CONSENT_TEXT,
};

/**
 * The version to store for what a client says it showed.
 *
 * An unknown or absent version is stored as the CURRENT one rather than
 * refused: the box was ticked, and failing a signup over a label would be
 * absurd. But it is never stored verbatim — a client must not be able to write
 * arbitrary text into the consent record.
 */
export function resolveConsentVersion(sent: unknown): string {
  const v = typeof sent === 'string' ? sent.trim() : '';
  return v && Object.prototype.hasOwnProperty.call(KNOWN_CALL_CONSENT_VERSIONS, v) ? v : CALL_CONSENT_TEXT_VERSION;
}

/**
 * The same kind of consent, given on the www.limecall.com number checkout's
 * "Verify your mobile" step — before any account exists, so it is stored on
 * checkout_attempts (call_consent_at / call_consent_version), not in
 * call_consents. Different words, so its own version: consent is to the
 * sentence shown. Copied verbatim into limecall-astro
 * (src/pages/get-number/checkout.astro); keep the two in step.
 */
export const CHECKOUT_CALL_CONSENT_TEXT =
  'Call or text me about this order if I don\u2019t finish, including with an automated or AI voice. Optional \u2014 you can opt out any time.';

export const CHECKOUT_CALL_CONSENT_TEXT_VERSION = 'checkout-v1-2026-10-10';

export const KNOWN_CHECKOUT_CALL_CONSENT_VERSIONS: Readonly<Record<string, string>> = {
  [CHECKOUT_CALL_CONSENT_TEXT_VERSION]: CHECKOUT_CALL_CONSENT_TEXT,
};

/** As resolveConsentVersion, for the checkout sentence. */
export function resolveCheckoutConsentVersion(sent: unknown): string {
  const v = typeof sent === 'string' ? sent.trim() : '';
  return v && Object.prototype.hasOwnProperty.call(KNOWN_CHECKOUT_CALL_CONSENT_VERSIONS, v) ? v : CHECKOUT_CALL_CONSENT_TEXT_VERSION;
}
